import type { ScheduleItem } from '../types/index.js'
import { scheduleItemTitle } from './utils.js'

/**
 * Seuil de détection. À l'antenne, une seconde sans élément programmé est déjà
 * un trou : le moteur y bascule sur son bouche-trou, donc l'opérateur doit le
 * voir et pouvoir le combler lui-même. On descend au plus bas que la grille
 * sache exprimer — les horaires sont stockés à la seconde.
 */
export const GAP_THRESHOLD_SEC = 1

export interface GridGap {
  /** ISO de la fin de l'élément précédent — début du trou. */
  startTime:     string
  /** ISO du début de l'élément suivant — fin du trou. */
  endTime:       string
  durationSec:   number
  /** Titre de l'élément qui se termine juste avant le trou. */
  previousItemTitle: string | null
  /** Titre de l'élément qui suit le trou, pour situer l'opérateur. */
  nextItemTitle: string | null
  gridId:        string
}

/**
 * Trous de la journée affichée, calculés depuis les éléments déjà en mémoire.
 *
 * Seuls les écarts *entre deux éléments* comptent. Un début de grille à 6 h ne
 * laisse pas un trou de six heures avant lui : la chaîne n'émet simplement pas
 * encore, et le signaler à chaque ouverture rendrait l'alerte inutile.
 */
export function findGridGaps(items: ScheduleItem[], thresholdSec = GAP_THRESHOLD_SEC): GridGap[] {
  const sorted = [...items]
    .filter(item => item.status !== 'SKIPPED')
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())

  const gaps: GridGap[] = []
  // On suit la fin la plus tardive rencontrée, pas celle de l'élément
  // précédent : un élément court entièrement contenu dans un plus long ne doit
  // pas ouvrir un trou fantôme derrière lui.
  let coveredUntil = -Infinity
  let coveringItem: ScheduleItem | null = null

  for (const item of sorted) {
    const start = new Date(item.startTime).getTime()
    const end   = new Date(item.endTime).getTime()

    if (Number.isFinite(coveredUntil) && start - coveredUntil >= thresholdSec * 1000) {
      gaps.push({
        startTime:     new Date(coveredUntil).toISOString(),
        endTime:       item.startTime,
        durationSec:   Math.round((start - coveredUntil) / 1000),
        previousItemTitle: coveringItem ? scheduleItemTitle(coveringItem) : null,
        nextItemTitle: scheduleItemTitle(item),
        gridId:        item.gridId,
      })
    }

    if (end > coveredUntil) {
      coveredUntil = end
      coveringItem = item
    }
  }

  return gaps
}
